/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { FormControl, Grid, InputLabel, MenuItem, Select } from "@mui/material";
import { TextFieldWithHelperText } from "../../../components/TextFieldWithHelperText";
import {
  submitRequestDialogStyles as styles,
  default as commonStyles,
} from "../styles";
import ConvoyDialog from "../../../components/ConvoyDialog";
import { toCamelCase } from "../../../utils/helperFunctions";
import { useGetEnvs } from "../../../api/envs";
import { useSubmitDbSoftwareBuild } from "../../../api/softwareImages";
import ScriptUploadInput from "./ScriptUploadInput";

const SubmitDbPatchRequestDialog = ({
  open,
  resetDialog,
  callback,
  displayNotification,
  initPayload,
  availableClouds,
}) => {
  const [payload, setPayload] = useState({});

  const {
    postData: submitDbSoftwareBuild,
    response: submitDbSoftwareBuildResp,
    responseStatus: submitDbSoftwareBuildStatus,
  } = useSubmitDbSoftwareBuild();

  const { postData: getEnvs, response: getEnvsResp } = useGetEnvs();

  useEffect(() => {
    if (open) {
      getEnvs();
      setPayload(structuredClone(initPayload));
    } else {
      setPayload({});
    }
  }, [open, initPayload]);

  const handleDialogConfirm = () => {
    availableClouds?.forEach((cloud) => {
      if (payload?.tenant?.[cloud.toUpperCase()] === "No Tenant") {
        delete payload.tenant[cloud.toUpperCase()];
      }
    });
    submitDbSoftwareBuild(payload);
    resetDialog();
  };

  /**
   * @param {string} label
   * @returns {object} */
  const textFieldCommonProps = (label) => ({
    fullWidth: true,
    label: label,
    color: "secondary",
    value: payload?.[toCamelCase(label)] || "",
    onChange: (e) => {
      setPayload((prev) => ({
        ...prev,
        [toCamelCase(label)]: e.target.value,
      }));
    },
  });

  useEffect(() => {
    displayNotification(
      submitDbSoftwareBuildResp,
      submitDbSoftwareBuildStatus,
      callback
    );
  }, [submitDbSoftwareBuildResp, submitDbSoftwareBuildStatus]);

  const isValidPayload = useMemo(() => {
    if (
      !payload?.jiraId ||
      !payload?.patchVersion ||
      !payload?.patchVersionName ||
      !payload?.installationScript ||
      !payload?.validationScript
    ) {
      return false;
    }
    return true;
  }, [payload]);

  return (
    <ConvoyDialog
      open={open}
      title="Submit DB Patch Request"
      handleDialogConfirm={handleDialogConfirm}
      resetDialog={resetDialog}
      disabled={!isValidPayload}
    >
      <Grid container spacing={1} sx={styles.gridContainer}>
        <Grid item xs={4}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Engine")}
            disabled
          />
        </Grid>
        <Grid item xs={4}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Major Version Name")}
            disabled
          />
        </Grid>
        <Grid item xs={4}>
          <TextFieldWithHelperText {...textFieldCommonProps("Jira Id")} />
        </Grid>
        <Grid item xs={6}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Old Patch Version")}
            disabled
          />
        </Grid>

        <Grid item xs={6}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Patch Version")}
            label="New Patch Version"
            onChange={(e) => {
              setPayload((prev) => {
                const newPatchVersionName =
                  prev?.oldPatchVersionName?.replaceAll(
                    prev?.oldPatchVersion,
                    e.target.value
                  );

                const out = {
                  ...prev,
                  patchVersion: e.target.value,
                };
                if (newPatchVersionName !== prev?.oldPatchVersionName) {
                  out.patchVersionName = newPatchVersionName;
                }
                return out;
              });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Old Patch Version Name")}
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <TextFieldWithHelperText
            {...textFieldCommonProps("Patch Version Name")}
            label="New Patch Version Name"
          />
        </Grid>
        {["installation", "validation"].map((scriptType) => (
          <Grid item xs={6} key={scriptType} sx={commonStyles.scriptBox}>
            <ScriptUploadInput
              scriptName={scriptType}
              setScript={(fileContent) =>
                setPayload((prev) => ({
                  ...prev,
                  [scriptType + "Script"]: fileContent,
                }))
              }
            />
          </Grid>
        ))}
        {availableClouds?.map((cloud, i) => (
          <Grid item xs={6} key={i}>
            <FormControl sx={styles.formControl}>
              <InputLabel size="string" color="secondary">
                {cloud?.toUpperCase()} Tenant
              </InputLabel>
              <Select
                fullWidth
                label={cloud?.toUpperCase() + " Tenant"}
                color="secondary"
                value={payload?.tenant?.[cloud.toUpperCase()]}
                onChange={(e) => {
                  setPayload((prev) => ({
                    ...prev,
                    tenant: {
                      ...prev?.tenant,
                      [cloud.toUpperCase()]: e.target.value,
                    },
                  }));
                }}
              >
                <MenuItem value={"No Tenant"}>No Tenant</MenuItem>
                {getEnvsResp?.response
                  ?.filter((tenant) => tenant?.status === "RUNNING")
                  .map((tenant, i) => (
                    <MenuItem value={tenant?.url} key={i}>
                      {tenant?.url}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        ))}
      </Grid>
    </ConvoyDialog>
  );
};

export default SubmitDbPatchRequestDialog;

export const useSubmitDbPatchRequestDialog = (
  callback = () => {},
  displayNotification = () => {}
) => {
  const [open, setOpen] = useState(false);
  const [initPayload, setInitPayload] = useState({});
  const [availableClouds, setAvailableClouds] = useState([]);

  return {
    submitDbPatchRequestDialog: (
      <SubmitDbPatchRequestDialog
        open={open}
        resetDialog={() => setOpen(false)}
        callback={callback}
        displayNotification={displayNotification}
        initPayload={initPayload}
        availableClouds={availableClouds}
      />
    ),
    setOpenSubmitDbPatchRequestDialog: (initPayload, availableClouds) => {
      setOpen(true);
      setInitPayload(initPayload || {});
      setAvailableClouds(availableClouds || []);
    },
  };
};
